import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject, takeUntil } from 'rxjs';
import {
  ExtendedKeycloakUser,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import SourcingStatusJSON from 'src/app/data/constants/SourcingStatus.json';
import { itemToTableDisplay } from 'src/app/modules/shared/utils/format-form-data-for-display';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_DATE_FORMAT } from 'src/app/data/constants/Data';
import _ from 'lodash';
import { MatSort } from '@angular/material/sort';

export interface Item {
  sourcingStatus: string;
  purchaseExecutiveName: string;
  brand: string;
  displayItemNumber: number;
  materialSubGroup: string;
  itemDescription: string;
  quantity: string;
  unitOfMeasurement: string;
  currency: string;
  unitPrice: string;
  totalAmount: string;
  partModelNumber: string;
  plannedDeliveryDate: string;
  criticality: string;
  isDisabled: boolean;
}
type ItemNumbers = Item['displayItemNumber'][];
type OptionalItemNumbers = ItemNumbers | undefined;

export enum SelectItemTableAction {
  EDIT,
  DELETE,
}

export type SelectItemTableColumn =
  | 'select'
  | 'sourcingStatus'
  | 'purchaseExecutiveName'
  | 'brand'
  | 'displayItemNumber'
  | 'materialSubGroup'
  | 'itemDescription'
  | 'quantity'
  | 'unitOfMeasurement'
  | 'currency'
  | 'unitPrice'
  | 'totalAmount'
  | 'partModelNumber'
  | 'plannedDeliveryDate'
  | 'criticality'
  | 'action';

@Component({
  selector: 'app-select-item-table',
  templateUrl: './select-item-table.component.html',
  styleUrls: ['./select-item-table.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    DecimalPipe,
    DatePipe,
  ],
})
export class SelectItemTableComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  // form: MyPurchaseForm | null = null;

  @Input() isScrollWithinTable: boolean = false;
  @Input() selectedItemNumbers: ItemNumbers = [];
  @Output() onSelectedChange = new EventEmitter<ItemNumbers>();
  @Input() actionsType?: SelectItemTableAction[];
  @Output() onRowEdit = new EventEmitter<Item>();
  @Output() onRowDelete = new EventEmitter<Item>();

  // itemSelection = new SelectionModel<Item>(true, []); // ??

  private _filterItemNumbers: OptionalItemNumbers;
  @Input()
  set filterItemNumbers(value: OptionalItemNumbers) {
    this._filterItemNumbers = value;
    // Convert @Input to observable
    // Check for value change, else infinite loop
    if (!_.isEqual(this.filterItemNumbersSubject.value, value)) {
      this.filterItemNumbersSubject.next(value);
    }
  }
  get filterItemNumbers(): OptionalItemNumbers {
    return this._filterItemNumbers;
  }
  private filterItemNumbersSubject = new BehaviorSubject<OptionalItemNumbers>(
    undefined
  );
  private filterItemNumbers$ = this.filterItemNumbersSubject.asObservable();
  @Input() isRemoveRowOnFilter?: boolean;

  itemDataSource: MatTableDataSource<Item> = new MatTableDataSource<Item>([]);
  @Input() itemDisplayColumns: SelectItemTableColumn[] = [
    'select',
    'sourcingStatus',
    'purchaseExecutiveName',
    'brand',
    'displayItemNumber',
    'materialSubGroup',
    'itemDescription',
    'quantity',
    'unitOfMeasurement',
    'currency',
    'unitPrice',
    'totalAmount',
    'partModelNumber',
    'plannedDeliveryDate',
    'criticality',
    'action',
  ];
  pageSizeOptions: number[] = [5, 10, 20];
  pageSizeUniqueKey = 'pageSize-SelectItemTable-item';
  defaultPageSize = 5;

  private itemPaginator!: MatPaginator;

  @ViewChild('itemPaginator', { read: MatPaginator })
  set matItemPaginator(mp: MatPaginator) {
    this.itemPaginator = mp;
    this.itemDataSource.paginator = this.itemPaginator;
  }

  private itemSort!: MatSort;
  @ViewChild('itemTableSort', { read: MatSort })
  set matItemSort(ms: MatSort) {
    this.itemSort = ms;
    this.itemDataSource.sort = this.itemSort;
  }

  constructor(
    private store: Store<{
      user: UserInformation;
      form: MyPurchaseForm;
      dp: StoreDropdowns;
    }>,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
  ) {
    const storedPageSize = localStorage.getItem(this.pageSizeUniqueKey);
    if (storedPageSize) this.defaultPageSize = Number(storedPageSize);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select('form'),
      this.filterItemNumbers$,
      this.store.select('dp'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([form, filterItemNumbers, dp]: [
          MyPurchaseForm,
          OptionalItemNumbers,
          StoreDropdowns
        ]) => {
          if (form.documentNo) {
            let itemList = form.ItemList;

            if (this.isRemoveRowOnFilter && Array.isArray(filterItemNumbers)) {
              itemList = itemList.filter((item) =>
                filterItemNumbers.includes(item.AddItems.displayItemNumber)
              );
            }

            const formattedItemList = this.formatItemList(
              itemList,
              filterItemNumbers,
              dp.UserList as ExtendedKeycloakUser[]
            );

            this.itemDataSource.data = formattedItemList;
          }
        }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private formatItemList(
    itemList: MyPurchaseForm['ItemList'],
    filterItemNumbers: OptionalItemNumbers,
    userList: ExtendedKeycloakUser[]
  ) {
    const formattedItemList = itemList
      .filter((item) => {
        const { isDeleted, Strikethrough } = item;
        return !isDeleted && !Strikethrough;
      })
      .map((item) => {
        const { displayItemNumber, SourcingStatusId, PurchaseExecutive } =
          item.AddItems;

        const displayItem = itemToTableDisplay(item, this.decimalPipe);

        const purchaseExecutiveName =
          userList.find((user) => {
            return user.id === PurchaseExecutive;
          })?.last_name ?? '-';

        const formattedItem: Item = {
          displayItemNumber: displayItem.displayItemNumber,
          itemDescription: displayItem.ItemDescription ?? '-',
          sourcingStatus: SourcingStatusId
            ? this.getSourcingStatusNameFromId(SourcingStatusId)
            : '-',
          purchaseExecutiveName: purchaseExecutiveName,
          brand: displayItem.Brand ?? '-',
          materialSubGroup: displayItem.MaterialSubGroupNameId ?? '-',
          quantity: displayItem.Quantity,
          unitOfMeasurement: displayItem.UnitOfMeasurement,
          currency: displayItem.Currency,
          unitPrice: displayItem.UnitPrice,
          totalAmount: displayItem.TotalAmount,
          partModelNumber: displayItem.PartModelNumber,
          plannedDeliveryDate: this.formatDate(displayItem.PlannedDeliveryDate),
          criticality: displayItem.Criticality ?? '-',
          isDisabled: Array.isArray(filterItemNumbers)
            ? !filterItemNumbers.includes(displayItemNumber)
            : false,
        };

        return formattedItem;
      });

    return formattedItemList;
  }

  getNonDisabled(list: Item[]) {
    return list.filter((row) => !row.isDisabled);
  }

  isAllItemsSelected() {
    return this.getNonDisabled(this.itemDataSource.data).every((item) =>
      this.selectedItemNumbers.includes(item.displayItemNumber)
    );
  }

  toggleAllItemsRows() {
    if (this.isAllItemsSelected()) {
      // Clear selection
      this.onSelectedChange.emit([]);
      return;
    }

    // Select all non-disabled
    this.onSelectedChange.emit(
      this.getNonDisabled(this.itemDataSource.data).map(
        (item) => item.displayItemNumber
      )
    );
  }

  toggleItemRow(item: Item) {
    if (item.isDisabled) {
      return;
    }

    const { displayItemNumber } = item;
    const isSelected = this.isItemSelected(item);

    if (isSelected) {
      // De-select row
      this.onSelectedChange.emit(
        this.selectedItemNumbers.filter(
          (itemNumber) => itemNumber !== displayItemNumber
        )
      );
    } else {
      // Select row
      this.onSelectedChange.emit([
        ...this.selectedItemNumbers,
        displayItemNumber,
      ]);
    }
  }

  isItemSelected(item: Item) {
    const { displayItemNumber } = item;
    return this.selectedItemNumbers.includes(displayItemNumber);
  }

  hasSelectedItem() {
    return this.selectedItemNumbers.length > 0;
  }

  onRowEditClick($event: Item) {
    this.onRowEdit.emit($event);
  }

  onRowDeleteClick($event: Item) {
    this.onRowDelete.emit($event);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.itemDataSource.filter = filterValue.trim().toLowerCase();

    if (this.itemDataSource.paginator) {
      this.itemDataSource.paginator.firstPage();
    }
  }

  private getSourcingStatusNameFromId(sourcingStatusId: number) {
    const foundSourcingStatus = SourcingStatusJSON.find(
      (entry) => entry.SourcingStatusId === sourcingStatusId
    );

    return foundSourcingStatus ? foundSourcingStatus.name : '';
  }

  private formatDate(date: any): string {
    if (date) {
      return this.datePipe.transform(date, 'dd/MM/yyyy') ?? date;
    }
    return date;
  }

  public onPaginatorPage(event: PageEvent) {
    const { pageSize } = event;

    localStorage.setItem(this.pageSizeUniqueKey, pageSize.toString());
  }

  public get SelectItemTableAction(): typeof SelectItemTableAction {
    return SelectItemTableAction;
  }
}
